<template>
  <el-dialog
    title="修改最终首付比"
    :visible.sync="prepayAdjustDialog"
    width="900px"
    class="prepay-adjust-dialog"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="!prepayAdjustSaving"
    :show-close="!prepayAdjustSaving"
  >
    <div class="prepay-adjust-body" v-loading="prepayAdjustLoading">
      <!-- 同一类目支持多选，再次点击已选标签可取消。 -->
      <div class="adjust-groups">
        <div
          v-if="!prepayAdjustLoading && !prepayAdjustGroupList.length"
          class="detail-empty"
        >
          {{
            prepayAdjustLoadFailed
              ? "加载失败，请关闭弹框后重试"
              : "暂无可用人工标签"
          }}
        </div>
        <div
          class="adjust-group"
          v-for="group in prepayAdjustGroupList"
          :key="group.key"
        >
          <div class="group-title">{{ group.label }}：</div>
          <el-checkbox-group
            v-model="prepayAdjustForm[group.key]"
            class="group-checkbox"
          >
            <el-checkbox-button
              v-for="opt in group.options"
              :key="opt.key"
              :label="opt.key"
              :disabled="prepayAdjustSaving || !opt.selectable"
            >
              {{ opt.label }}
            </el-checkbox-button>
          </el-checkbox-group>
        </div>
      </div>

      <!-- 右：调整明细。只列已勾选的项，一项没有时给引导语 -->
      <div class="adjust-detail">
        <div class="detail-title">调整明细</div>
        <div class="detail-list" v-if="prepayAdjustRows.length">
          <div
            class="detail-row"
            :class="{ 'is-positive': row.delta > 0 }"
            v-for="row in prepayAdjustRows"
            :key="row.id"
          >
            <span class="row-label">{{ row.label }}</span>
            <span class="row-value">
              <span class="row-value-label">最终首付比</span>
              <span class="row-value-num">{{ formatDelta(row.delta) }}</span>
            </span>
          </div>
        </div>
        <div class="detail-empty" v-else>
          选择左侧标签后，调整明细将显示在这里
        </div>

        <div class="detail-total">
          <span>合计：</span>
          <span
            class="total-num"
            :class="{ 'is-changed': prepayAdjustTotal !== 0 }"
          >
            {{ formatDelta(prepayAdjustTotal) }}
          </span>
        </div>
        <!-- 原始调整合计超过 ±20% 时提示，恰好达到边界时不提示。 -->
        <div class="detail-warn" v-if="prepayAdjustWarn">
          {{ prepayAdjustWarn }}
        </div>
      </div>
    </div>

    <div class="prepay-adjust-bottom">
      <div class="rate-preview">
        <span class="rate-label">系统首付比</span>
        <span class="rate-value rate-value--system">{{
          prepayAdjustSysRateText
        }}</span>
        <i class="el-icon-right rate-arrow"></i>
        <span class="rate-label" :class="{ 'is-changed': prepayAdjustChanged }"
          >最终首付比</span
        >
        <span class="rate-value" :class="{ 'is-changed': prepayAdjustChanged }">
          {{ prepayAdjustFinalRateText }}
        </span>
      </div>
      <div class="bottom-btns">
        <el-button
          @click="prepayAdjustDialog = false"
          :disabled="prepayAdjustSaving"
          size="small"
          >取 消</el-button
        >
        <!-- 权限码沿用入口那枚（原语义「修改最终评级」，要独立码得后端加种子数据） -->
        <el-button
          type="primary"
          size="small"
          :loading="prepayAdjustSaving"
          :disabled="!canSavePrepayAdjust"
          @click="savePrepayAdjust"
          >确认修改</el-button
        >
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { authBtnMixin } from "@/mixins/authBtnMixin";
import {
  PrepayAdjustLimit,
  PrepayAdjustWarnText,
  FinalPrepayRateRange,
} from "@/enum/dict";
import { getOrderManualRuleListAPI, updateFinalPrepayRateAPI } from "./api";

export default {
  name: "PrepayAdjustDialog",
  mixins: [authBtnMixin],
  props: {
    visible: { type: Boolean, default: false },
    orderNo: { type: String, default: "" },
    machinePrepayRate: { type: Number, default: null },
  },
  // 初始化选择状态和请求状态。
  data() {
    return {
      prepayAdjustForm: {},
      prepayAdjustGroupList: [],
      prepayAdjustLoading: false,
      prepayAdjustLoadFailed: false,
      prepayAdjustSaving: false,
      prepayAdjustVersion: 0,
    };
  },
  computed: {
    prepayAdjustDialog: {
      // 读取父组件的显示状态。
      get() {
        return this.visible;
      },
      // 通知父组件打开或关闭弹框。
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    // 按类目顺序生成已选标签明细。
    prepayAdjustRows() {
      return this.prepayAdjustGroupList.reduce((rows, group) => {
        const picked = this.prepayAdjustForm[group.key] || [];
        group.options.forEach((opt) => {
          if (picked.includes(opt.key))
            rows.push({ id: opt.key, label: opt.label, delta: opt.delta });
        });
        return rows;
      }, []);
    },
    // 累加标签的原始调整值。
    prepayAdjustRawTotal() {
      return this.prepayAdjustRows.reduce((sum, row) => sum + row.delta, 0);
    },
    // 将合计调整幅度限制在 ±20%。
    prepayAdjustTotal() {
      return Math.min(
        PrepayAdjustLimit,
        Math.max(-PrepayAdjustLimit, this.prepayAdjustRawTotal),
      );
    },
    // 原始合计越界时显示提示。
    prepayAdjustWarn() {
      if (this.prepayAdjustRawTotal < -PrepayAdjustLimit)
        return PrepayAdjustWarnText.min;
      if (this.prepayAdjustRawTotal > PrepayAdjustLimit)
        return PrepayAdjustWarnText.max;
      return "";
    },
    // 最终首付比预览限制在 20%～60%。
    prepayAdjustFinalRate() {
      return this.machinePrepayRate === null
        ? null
        : Math.min(
            FinalPrepayRateRange.max,
            Math.max(
              FinalPrepayRateRange.min,
              this.machinePrepayRate + this.prepayAdjustTotal,
            ),
          );
    },
    // 格式化机审首付比，缺失时显示占位符。
    prepayAdjustSysRateText() {
      return this.machinePrepayRate === null
        ? "--"
        : `${this.machinePrepayRate}%`;
    },
    // 格式化最终首付比预览。
    prepayAdjustFinalRateText() {
      return this.prepayAdjustFinalRate === null
        ? "--"
        : `${this.prepayAdjustFinalRate}%`;
    },
    // 判断最终展示值是否变化。
    prepayAdjustChanged() {
      return (
        this.prepayAdjustFinalRate !== null &&
        this.prepayAdjustFinalRate !== this.machinePrepayRate
      );
    },
    // 权限和订单号由父组件在打开前检查，机审首付比也由父组件统一转换。
    // 保存时检查权限、系统首付比和请求状态。
    // 至少需存在一个可选标签；不要求已选标签或首付比发生变化，允许提交空列表清除人工调整。
    canSavePrepayAdjust() {
      return !!(
        this.isShowBtn(this.AUTH_BTN.review_list_detail_update_final_grade) &&
        this.machinePrepayRate !== null &&
        !this.prepayAdjustSaving &&
        !this.prepayAdjustLoading &&
        !this.prepayAdjustLoadFailed &&
        this.prepayAdjustGroupList.some((group) =>
          group.options.some((option) => option.selectable),
        )
      );
    },
  },
  watch: {
    visible: {
      immediate: true,
      // 打开时加载标签，关闭时清空状态。
      handler(value) {
        if (value) this.openPrepayAdjust();
        else this.resetPrepayAdjust();
      },
    },
    // 切换订单时关闭弹框并使旧请求失效。
    orderNo() {
      this.prepayAdjustDialog = false;
      this.resetPrepayAdjust();
    },
    // 路由切换时立即清理，避免旧订单响应回填。
    "$route.params.id"() {
      this.prepayAdjustDialog = false;
      this.resetPrepayAdjust();
    },
  },
  // 销毁时使未完成的请求失效。
  beforeDestroy() {
    this.resetPrepayAdjust();
  },
  // 页面失活时关闭并清空弹框。
  deactivated() {
    this.prepayAdjustDialog = false;
    this.resetPrepayAdjust();
  },
  methods: {
    // 加载当前订单命中的人工标签。
    async openPrepayAdjust() {
      this.resetPrepayAdjust();
      this.prepayAdjustLoading = true;
      const version = this.prepayAdjustVersion;
      const orderNo = this.orderNo;
      try {
        const data = await getOrderManualRuleListAPI(orderNo);
        if (version !== this.prepayAdjustVersion || orderNo !== this.orderNo)
          return;
        this.prepayAdjustGroupList = (Array.isArray(data) ? data : [])
          .map((group, index) => ({
            key: String(index),
            label: group.tagCategory,
            options: (group.ruleGroupList || []).map((rule) => {
              const id = Number(rule.id);
              const delta = Number(rule.adjustRate);
              return {
                key: id,
                label: rule.tagName,
                selectable: rule.selectable === true,
                selected: rule.selected === true,
                delta,
                displayContent: rule.displayContent,
                hitRemark: rule.hitRemark,
              };
            }),
          }))
          .filter((group) => group.options.length);
        // 回显已选规则；selectable 只控制是否允许点击。
        this.prepayAdjustForm = this.prepayAdjustGroupList.reduce(
          (form, group) => {
            form[group.key] = group.options
              .filter((option) => option.selected)
              .map((option) => option.key);
            return form;
          },
          {},
        );
      } catch (error) {
        if (version === this.prepayAdjustVersion)
          this.prepayAdjustLoadFailed = true;
      } finally {
        if (version === this.prepayAdjustVersion)
          this.prepayAdjustLoading = false;
      }
    },
    // 清空选择和请求状态，并更新请求版本。
    resetPrepayAdjust() {
      this.prepayAdjustVersion += 1;
      this.prepayAdjustForm = {};
      this.prepayAdjustGroupList = [];
      this.prepayAdjustLoading = false;
      this.prepayAdjustLoadFailed = false;
      this.prepayAdjustSaving = false;
    },
    // 将调整值格式化为带符号的百分比。
    formatDelta(delta) {
      return `${delta > 0 ? "+" : ""}${delta}%`;
    },
    // 提交规则组 ID，成功后通知父组件刷新。
    async savePrepayAdjust() {
      if (!this.canSavePrepayAdjust) return;
      const version = this.prepayAdjustVersion;
      const orderNo = this.orderNo;
      this.prepayAdjustSaving = true;
      try {
        const result = await updateFinalPrepayRateAPI({
          orderNo,
          ruleGroupIdList: [
            ...new Set(this.prepayAdjustRows.map((row) => row.id)),
          ],
        });
        if (version !== this.prepayAdjustVersion || orderNo !== this.orderNo)
          return;
        if (result !== true) {
          this.$message.error("修改失败，请重试");
          return;
        }
        this.$message.success("修改成功");
        this.prepayAdjustDialog = false;
        this.$emit("success");
      } catch (error) {
        // 接口错误由 http 统一提示。
      } finally {
        if (version === this.prepayAdjustVersion)
          this.prepayAdjustSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.prepay-adjust-dialog /deep/ {
  .el-dialog {
    max-width: calc(100vw - 32px);
  }
  .el-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__headerbtn {
    position: static;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 16px;
    line-height: 1;
  }

  .el-dialog__title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__body {
    padding: 16px 40px 22px;
    font-size: 14px;
  }

  .prepay-adjust-body {
    display: flex;
    align-items: stretch;
  }

  .adjust-groups {
    width: 50%;
    min-width: 0;
    box-sizing: border-box;
    padding: 22px 24px 0 10px;
  }

  .adjust-group {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 0;
    }

    .group-title {
      margin-bottom: 12px;
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      color: #303133;
    }
  }

  .group-checkbox {
    display: flex;
    flex-wrap: wrap;

    .el-checkbox-button {
      margin: 0 10px 10px 0;
    }

    .el-checkbox-button .el-checkbox-button__inner {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 64px;
      height: 34px;
      padding: 0 14px;
      border: none;
      border-radius: 17px;
      font-size: 14px;
      line-height: 1;
      background: #f5f5f5;
      color: #a6a6a6;
      box-shadow: none;
    }

    .el-checkbox-button__original:checked + .el-checkbox-button__inner {
      background-color: #409eff;
      color: #fff;
      box-shadow: none;
    }

    .el-checkbox-button.is-disabled .el-checkbox-button__inner,
    .el-checkbox-button.is-disabled .el-checkbox-button__original:checked + .el-checkbox-button__inner {
      background-color: #f8f9fa;
      color: #dcdfe6;
      border: none;
      box-shadow: none;
      cursor: not-allowed;
    }

    .el-checkbox-button:focus:not(.is-focus):not(:active):not(.is-disabled) {
      box-shadow: none !important;
      outline: none !important;
    }
  }

  .adjust-detail {
    display: flex;
    flex-direction: column;
    width: 50%;
    min-width: 0;
    min-height: 354px;
    box-sizing: border-box;
    padding: 24px 30px;
    background: #fafafa;
    border-radius: 16px;

    .detail-title {
      margin-bottom: 24px;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      color: #303133;
    }

    // 空状态和明细状态的合计都固定在面板底部。
    .detail-list {
      flex: 1;
    }

    .detail-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      min-height: 44px;
      padding: 10px 8px;
      box-sizing: border-box;
      margin-bottom: 10px;
      background: #fff7ec;
      border-left: 2px solid #ff9900;
      border-radius: 4px;

      &.is-positive {
        background: #f0fdf4;
        border-left-color: #22c55e;

        .row-value {
          color: #16a34a;
        }
      }

      .row-label {
        min-width: 0;
        overflow-wrap: anywhere;
        font-size: 14px;
        color: #303133;
      }

      .row-value {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        color: #fa8c16;
      }

      .row-value-label {
        font-size: 14px;
        color: inherit;
        margin-right: 10px;
      }

      .row-value-num {
        font-size: 14px;
        font-weight: 500;
        color: inherit;
      }
    }

    .detail-empty {
      flex: 1;
      font-size: 14px;
      color: #c0c4cc;
      text-align: center;
    }

    .detail-total {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 20px;
      padding-top: 14px;
      border-top: 1px solid #ebeef5;
      font-size: 14px;
      font-weight: 600;
      color: #303133;

      .total-num.is-changed {
        color: #ff4d2d;
      }
    }

    .detail-warn {
      margin-top: 18px;
      font-size: 14px;
      line-height: 22px;
      color: #ff4d2d;
      text-align: center;
    }
  }

  .prepay-adjust-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 22px -40px 0;
    padding: 20px 42px 0 22px;
    border-top: 1px solid #ebeef5;

    .rate-preview {
      display: flex;
      align-items: center;
      font-size: 14px;

      .rate-label {
        color: #606266;
        margin-right: 6px;
        &.is-changed {
          color: #ff4d2d;
        }
      }

      .rate-value {
        font-size: 18px;
        font-weight: 700;
        color: #303133;

        &.rate-value--system {
          font-size: 14px;
          font-weight: 400;
        }

        &.is-changed {
          color: #ff4d2d;
        }
      }

      .rate-arrow {
        margin: 0 14px;
        font-size: 20px;
        color: #303133;
      }
    }

    .bottom-btns {
      display: flex;
      align-items: center;

      .el-button {
        height: 34px;
        padding: 0 18px;
        border: none;
        border-radius: 3px;
        font-size: 14px;
      }

      .el-button--default {
        background: #f5f6f7;
        color: #a6a6a6;

        &:hover,
        &:focus {
          background: #e6e8eb;
          color: #606266;
        }
      }
    }
  }
}
</style>
